import { FC } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

export interface DonutChartDatum {
  key: string;
  label: string;
  value: number;
  color: string;
}

interface TooltipPayloadEntry {
  payload: DonutChartDatum;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
}

interface Props {
  data: DonutChartDatum[];
  valueFormatter?: (value: number) => string;
  height?: number;
}

const DonutChart: FC<Props> = ({
  data,
  valueFormatter = (v) => `${v}`,
  height = 260,
}) => {
  const nonZero = data.filter((d) => d.value > 0);

  if (!nonZero.length) {
    return (
      <div className="text-center text-muted py-5">
        Aucune donnée à afficher.
      </div>
    );
  }

  const total = nonZero.reduce((sum, d) => sum + d.value, 0);

  const CustomTooltip: FC<CustomTooltipProps> = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const { label, value, color } = payload[0].payload;
    const percent = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
    return (
      <div
        className="bg-white dark__bg-1100 border rounded shadow-sm px-2 py-1 fs--1"
        style={{ minWidth: 160 }}
      >
        <div className="d-flex align-items-center gap-1 fw-semibold mb-1">
          <span
            style={{
              display: "inline-block",
              width: 8,
              height: 8,
              background: color,
              borderRadius: 2,
            }}
          />
          {label}
        </div>
        <div className="d-flex justify-content-between gap-2">
          <span>{valueFormatter(value)}</span>
          <span className="text-muted">{percent}%</span>
        </div>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={nonZero}
          dataKey="value"
          nameKey="label"
          innerRadius="55%"
          outerRadius="80%"
          stroke="none"
        >
          {nonZero.map((d) => (
            <Cell key={d.key} fill={d.color} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default DonutChart;
